import React from 'react';
import Icon from '../../../components/AppIcon';

const VATCalculationSummary = ({ period, calculations, onRecalculate }) => {
  const {
    inputVAT = 0,
    outputVAT = 0,
    netLiability = 0,
    adjustments = 0,
    previousBalance = 0,
    totalDue = 0
  } = calculations;

  const summaryItems = [
    {
      label: 'Output VAT (Sales)',
      value: outputVAT,
      description: 'VAT collected on sales',
      icon: 'TrendingUp',
      color: 'text-success'
    },
    {
      label: 'Input VAT (Purchases)',
      value: inputVAT,
      description: 'VAT paid on purchases',
      icon: 'TrendingDown',
      color: 'text-primary'
    },
    {
      label: 'Net VAT Liability',
      value: netLiability,
      description: 'Output VAT minus Input VAT',
      icon: 'Calculator',
      color: netLiability >= 0 ? 'text-warning' : 'text-success'
    },
    {
      label: 'Adjustments',
      value: adjustments,
      description: 'Manual adjustments and corrections',
      icon: 'Edit',
      color: 'text-muted-foreground'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">VAT Calculation Summary</h3>
          <p className="text-sm text-muted-foreground">{period}</p>
        </div>
        <button
          onClick={onRecalculate}
          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors"
        >
          <Icon name="RefreshCw" size={16} />
          <span>Recalculate</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {summaryItems?.map((item, index) => (
          <div key={index} className="p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name={item?.icon} size={16} className={item?.color} />
                <span className="text-sm font-medium text-foreground">{item?.label}</span>
              </div>
              <span className={`text-lg font-bold ${item?.color}`}>
                ${Math.abs(item?.value)?.toLocaleString()}
                {item?.value < 0 && ' CR'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{item?.description}</p>
          </div>
        ))}
      </div>
      {/* Total Due Section */}
      <div className="border-t border-border pt-4">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground">Total Amount Due</h4>
              <p className="text-sm text-muted-foreground">
                Including previous balance and adjustments
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">
                ${totalDue?.toLocaleString()}
              </p>
              {previousBalance !== 0 && (
                <p className="text-xs text-muted-foreground">
                  Previous balance: ${previousBalance?.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Breakdown Details */}
      <div className="mt-4 p-4 bg-muted/10 rounded-lg">
        <h5 className="text-sm font-medium text-foreground mb-3">Calculation Breakdown</h5>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Output VAT</span>
            <span className="text-foreground">${outputVAT?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Input VAT</span>
            <span className="text-foreground">-${inputVAT?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-t border-border pt-2">
            <span className="text-muted-foreground">Net VAT Liability</span>
            <span className="font-medium text-foreground">${netLiability?.toLocaleString()}</span>
          </div>
          {adjustments !== 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Adjustments</span>
              <span className="text-foreground">
                {adjustments >= 0 ? '+' : ''}${adjustments?.toLocaleString()}
              </span>
            </div>
          )}
          {previousBalance !== 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Previous Balance</span>
              <span className="text-foreground">${previousBalance?.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-2 font-semibold">
            <span className="text-foreground">Total Due</span>
            <span className="text-primary">${totalDue?.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VATCalculationSummary;